import {
	Box,
	Button,
	Flex,
	Text,
	Table,
	Tbody,
	Tr,
	Td,
	Input,
	InputGroup,
	InputLeftElement,
	Alert,
	AlertIcon,
} from 'src/components/ui';
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { generateBudgetBreakdownByPlacements } from 'src/components/campaign/CampaignBudget/parts/utils';
import { CampaignContext } from 'src/contexts';
import { CHANNELS_NAME_MAPPING } from 'src/lib/constants';
import { IChannelGroup } from 'src/lib/schemas';
import { ICampaignBudget } from 'src/lib/schemas/campaign/newFlowCampaign';
import { createOrUpdateCampaign } from 'src/services/campaign';

interface IBudgetBreakdownProps {
	budget?: ICampaignBudget;
	availableChannelGroups: IChannelGroup[] | null;
	setIsValidCampaignBudget: (val: boolean) => void;
}

const formatAmount = (amount: number) => {
	return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
};

const BudgetBreakdown = ({
	budget,
	availableChannelGroups,
	setIsValidCampaignBudget,
}: IBudgetBreakdownProps) => {
	const { id: campaignId, campaign } = useContext(CampaignContext);
	const [isEditing, setIsEditing] = useState(false);
	const [editedChannels, setEditedChannels] = useState(
		() =>
			budget?.breakdown?.map((item) => ({
				id: item.id,
				name: CHANNELS_NAME_MAPPING[item.id],
				amount: item.budget,
			})) || [],
	);
	const [editedBreakdown, setEditedBreakdown] = useState(
		() => budget?.breakdown || [],
	);

	const [allocationError, setAllocationError] = useState<string | null>(null);

	const remainingBudget = useMemo(() => {
		const totalAssigned = editedChannels.reduce(
			(total, channel) => total + channel.amount,
			0,
		);
		return (budget?.total ?? 0) - totalAssigned;
	}, [editedChannels, budget?.total]);

	useEffect(() => {
		setIsValidCampaignBudget(!allocationError);
	}, [allocationError, setIsValidCampaignBudget]);

	useEffect(() => {
		const tolerance = 0.01;
		if (remainingBudget > tolerance) {
			setAllocationError(
				`Pending to allocate $${formatAmount(remainingBudget)}`,
			);
		} else if (remainingBudget < -tolerance) {
			setAllocationError(
				`Over budget by $${formatAmount(Math.abs(remainingBudget))}`,
			);
		} else {
			setAllocationError(null);
		}
	}, [remainingBudget]);

	const generatedAIAllocation = useMemo(() => {
		const placements = campaign?.placements ?? [];
		const total = budget?.total ?? 0;

		// Don't regenerate if no placements or budget
		if (placements.length === 0 || total === 0) {
			return [];
		}

		return generateBudgetBreakdownByPlacements(
			placements,
			availableChannelGroups ?? [],
			total,
		);
	}, [campaign?.placements, availableChannelGroups, budget?.total]);

	const isBudgetItemVisible = (item: any) => {
		return !availableChannelGroups?.some((group) =>
			group.channels.some(
				(channel) => channel.hidden_budget && channel.id === item.id,
			),
		);
	};

	// Reset editing mode when placements change to show updated AI allocation
	useEffect(() => {
		setIsEditing(false);
	}, [campaign?.placements]);

	// Single source of truth for editedChannels
	const prevGeneratedAllocationRef = useRef<any[]>([]);
	const prevBudgetBreakdownRef = useRef<any[]>([]);

	useEffect(() => {
		if (isEditing) {
			// In editing mode, only update from saved budget breakdown if it actually changed
			const hasBudgetBreakdownChanged =
				JSON.stringify(prevBudgetBreakdownRef.current) !==
				JSON.stringify(budget?.breakdown);

			if (budget?.breakdown?.length && hasBudgetBreakdownChanged) {
				setEditedChannels(
					budget.breakdown?.map((item) => ({
						id: item.id,
						name: CHANNELS_NAME_MAPPING[item.id],
						amount: item.budget,
						hidden_budget: item.hidden_budget,
					})) || [],
				);
				setEditedBreakdown(budget.breakdown || []);
				prevBudgetBreakdownRef.current = budget.breakdown;
			}
		} else {
			// Not in editing mode, use AI allocation only if it actually changed
			const hasGeneratedAllocationChanged =
				JSON.stringify(prevGeneratedAllocationRef.current) !==
				JSON.stringify(generatedAIAllocation);

			if (hasGeneratedAllocationChanged && generatedAIAllocation.length > 0) {
				setEditedChannels(
					generatedAIAllocation.map((item) => ({
						id: item.id,
						name: CHANNELS_NAME_MAPPING[item.id],
						amount: item.budget,
						hidden_budget: false,
					})),
				);
				setEditedBreakdown(generatedAIAllocation);
				prevGeneratedAllocationRef.current = generatedAIAllocation;
			}
		}
	}, [isEditing, generatedAIAllocation, budget?.breakdown]);

	const updateCampaignRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (!campaignId || !budget) return;

		const newBreakdown = isEditing ? editedBreakdown : generatedAIAllocation;

		// Clear previous timeout to prevent multiple calls
		if (updateCampaignRef.current) {
			clearTimeout(updateCampaignRef.current);
		}

		const updateCampaign = async () => {
			try {
				await createOrUpdateCampaign(
					{
						budget: {
							...budget,
							breakdown: newBreakdown,
						},
					},
					campaignId,
				);
			} catch (error) {
				console.error('Error updating campaign:', error);
			}
		};

		// Only update if in editing mode or breakdown actually changed
		if (isEditing) {
			updateCampaignRef.current = setTimeout(() => {
				updateCampaign();
				updateCampaignRef.current = null;
			}, 800);
		}

		return () => {
			if (updateCampaignRef.current) {
				clearTimeout(updateCampaignRef.current);
				updateCampaignRef.current = null;
			}
		};
	}, [editedBreakdown, campaignId, isEditing]);

	const handleManualDefineClick = () => {
		if (isEditing) {
			setEditedChannels(
				generatedAIAllocation.map((item) => ({
					id: item.id,
					name: CHANNELS_NAME_MAPPING[item.id],
					amount: item.budget,
				})),
			);
			setEditedBreakdown(generatedAIAllocation);
		}
		setIsEditing(!isEditing);
	};

	const handleAmountChange = useCallback(
		(id: string, value: string) => {
			const parsedValue = value === '' ? 0 : parseFloat(value);

			setEditedChannels((prevChannels) =>
				prevChannels.map((channel) =>
					channel.id === id ? { ...channel, amount: parsedValue } : channel,
				),
			);

			setEditedBreakdown((prevBreakdown) =>
				prevBreakdown.map((item) =>
					item.id === id ? { ...item, budget: parsedValue } : item,
				),
			);
		},
		[],
	);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		const invalidChars = ['e', 'E', '+', '-', '.'];

		if (invalidChars.includes(e.key)) {
			e.preventDefault();
		}
	};

	const getPercentage = (itemBudget: number) =>
		budget?.total ? (itemBudget * 100) / budget.total : 0;

	const channels = editedChannels.map((item) => ({
		...item,
		percentage: getPercentage(item.amount),
	}));

	const getTotalAmount = () => {
		return channels.reduce((total, channel) => {
			return total + (channel.amount || 0);
		}, 0);
	};

	const getTotalPercentage = () => {
		const totalPercentage = channels.reduce((total, channel) => {
			return total + getPercentage(channel.amount);
		}, 0);
		return Math.min(totalPercentage, 100);
	};

	// Don't render if no paid channels are selected
	if (channels.length === 0) {
		return null;
	}

	return (
		<Box borderWidth="1px" borderRadius="md">
			<Flex
				direction={{ base: 'column', md: 'row' }}
				justifyContent="space-between"
				w="full"
				p={{ base: 3, md: 4 }}
				borderBottomWidth="1px"
				gap={{ base: 3, md: 0 }}
			>
				<Text size="md" fontWeight={500}>Budget breakdown</Text>
				<Button
					variant="orangeOutline"
					onClick={handleManualDefineClick}
					size={{ base: 'sm', md: 'md' }}
					w={{ base: 'full', md: 'auto' }}
				>
					{isEditing ? 'Revert to AI allocation' : 'Define manually'}
				</Button>
			</Flex>
			<Box p={{ base: 3, md: 4 }} bg="gray.50" borderRadius="md">
				{allocationError && (
					<Alert status="error" mb={4}>
						<AlertIcon />
						{allocationError}
					</Alert>
				)}

				{/* Mobile: Card-based layout */}
				<Box display={{ base: 'block', md: 'none' }}>
					<Flex direction="column" gap={2}>
						{channels
							.filter((channel) => isBudgetItemVisible(channel))
							.map((channel) => (
								<Box key={channel.id} bg="white" p={3} borderRadius="md" borderWidth="1px">
									<Flex justifyContent="space-between" alignItems="center" mb={2}>
										<Text fontSize="sm" fontWeight="medium">
											{channel.name} Ads
										</Text>
										<Text fontSize="sm" color="gray.600">
											{formatAmount(channel.percentage)}%
										</Text>
									</Flex>
									<Flex alignItems="center" gap={2}>
										{isEditing ? (
											<InputGroup flex={1}>
												<InputLeftElement
													pointerEvents="none"
													color="gray.300"
													fontSize="sm"
													children="$"
												/>
												<Input
													bg="white"
													borderRadius="md"
													type="number"
													value={
														channel.amount !== 0
															? String(channel.amount)
															: 0
													}
													onChange={(e) =>
														handleAmountChange(channel.id, e.target.value)
													}
													onKeyDown={handleKeyDown}
													size="sm"
													fontSize="sm"
													pl={8}
												/>
											</InputGroup>
										) : (
											<Text fontSize="lg" fontWeight="bold" color="#F7480B">
												${formatAmount(channel.amount)}
											</Text>
										)}
									</Flex>
								</Box>
							))}

						{/* Total row for mobile */}
						<Box bg="gray.100" p={3} borderRadius="md" borderWidth="1px">
							<Flex justifyContent="space-between" alignItems="center" mb={2}>
								<Text fontSize="sm" fontWeight="bold">Total</Text>
								<Text fontSize="sm" fontWeight="bold" color="gray.600">
									{formatAmount(getTotalPercentage())}%
								</Text>
							</Flex>
							<Text fontSize="lg" fontWeight="bold">
								${formatAmount(getTotalAmount())}
							</Text>
						</Box>
					</Flex>
				</Box>

				{/* Desktop: Table layout */}
				<Box borderRadius="md" overflow="auto" borderWidth="1px" display={{ base: 'none', md: 'block' }}>
					<Table variant="simple" size="md">
						<Tbody bg="white">
							{channels
								.filter((channel) => isBudgetItemVisible(channel))
								.map((channel) => (
									<Tr key={channel.id}>
										<Td fontSize="sm" py={3}>
											{channel.name} Ads
										</Td>
										<Td isNumeric py={3}>
											{isEditing ? (
												<InputGroup justifyContent="flex-end" maxW="100px" float="right">
													<Input
														bg="white"
														borderRadius="md"
														type="number"
														value={
															channel.amount !== 0
																? String(channel.amount)
																: 0
														}
														textAlign="right"
														onChange={(e) =>
															handleAmountChange(channel.id, e.target.value)
														}
														onKeyDown={handleKeyDown}
														size="sm"
														fontSize="sm"
													/>
													<InputLeftElement
														alignSelf="center"
														pointerEvents="none"
														color="gray.300"
														fontSize="1em"
														children="$"
														top="50%"
														bottom="50%"
													/>
												</InputGroup>
											) : (
												<Text fontSize="sm">
													${formatAmount(channel.amount)}
												</Text>
											)}
										</Td>
										<Td isNumeric fontSize="sm" py={3}>
											{formatAmount(channel.percentage)}%
										</Td>
									</Tr>
								))}
						</Tbody>
						<Tbody bg="gray.100">
							<Tr>
								<Td fontSize="sm" fontWeight="bold" py={3}>
									Total
								</Td>
								<Td isNumeric fontSize="sm" fontWeight="bold" py={3}>
									${formatAmount(getTotalAmount())}
								</Td>
								<Td isNumeric fontSize="sm" fontWeight="bold" py={3}>
									{formatAmount(getTotalPercentage())}%
								</Td>
							</Tr>
						</Tbody>
					</Table>
				</Box>
			</Box>
		</Box>
	);
};

export default BudgetBreakdown;
